import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Smartphone, Share, X } from "lucide-react";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function InstallPWA({ className }) {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setGuide(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
      return;
    }
    const section = document.getElementById("install");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setGuide(true);
  };

  const steps = isIOS()
    ? { title: t("install.iosTitle"), items: [t("install.iosStep1"), t("install.iosStep2"), t("install.iosStep3")] }
    : { title: t("install.androidTitle"), items: [t("install.androidStep1"), t("install.androidStep2"), t("install.androidStep3")] };

  return (
    <>
      <button
        data-testid="install-pwa-btn"
        onClick={handleClick}
        className={
          className ||
          "flex items-center gap-1.5 h-10 px-3 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-[#1A1A1A] font-semibold text-sm transition-colors"
        }
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">{t("common.install")}</span>
      </button>

      <AnimatePresence>
        {guide && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setGuide(false)}
            data-testid="install-pwa-guide"
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 320 }}
              className="bg-white rounded-3xl w-full max-w-md p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="install-pwa-guide-close"
                onClick={() => setGuide(false)}
                className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-[#F0EBE1] flex items-center justify-center"
              >
                <X className="w-5 h-5 text-[#75807A]" />
              </button>
              <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
                {isIOS() ? <Share className="w-6 h-6 text-white" /> : <Smartphone className="w-6 h-6 text-white" />}
              </span>
              <h3 className="mt-4 font-head font-bold text-xl text-[#1A1A1A]">{steps.title}</h3>
              <ol className="mt-5 space-y-4">
                {steps.items.map((s, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[#F26419] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="text-[#3B423E] leading-relaxed pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
              <button
                data-testid="install-pwa-guide-gotit"
                onClick={() => setGuide(false)}
                className="mt-7 w-full py-3.5 rounded-2xl bg-[#1A1A1A] text-white font-bold"
              >
                {t("install.gotIt")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
